import React from "react";
import { useHistory } from "react-router-dom";
import { Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import * as actions from "@/redux/actions";
import SntImpersonateUserIcon from "@icons/SntImpersonateUserIcon";
import SntSupportUserIcon from "@icons/SntSupportUserIcon";
import SntSupportAdminIcon from "@icons/SntSupportAdminIcon";
import userClient from "@apis/userClient";
import useUser from "@hooks/useUser";
import SntButton from "@cleanup/widgets/forms/SntButton";
import useErrorDialog from "@cleanup/widgets/dialog/useErrorDialog";
import { RootState } from "@/types/RootState";
import { translate } from "@/hooks/useLanguage";
import { LanguageKey } from "@/types/generated/LanguageKey";

interface ImpersonateUser {
  supportMode?: boolean;
  adminMode?: boolean;
  hasAdminMode?: boolean;
}

interface ImpersonateHeaderProps {
  impersonateUser?: ImpersonateUser | Record<string, unknown>;
  onLeaveClicked?: () => void;
  /**
   * STIC-15481 R8: on the demo deployment this bar is always on screen, impersonating or
   * not, and turns green. Impersonation content is then prefixed rather than shown in a
   * second bar — one strip, so the layout offset stays the same in both states.
   */
  isDemoEnvironment?: boolean;
}

const ImpersonateHeader: React.FC<ImpersonateHeaderProps> = ({
  impersonateUser,
  onLeaveClicked,
  isDemoEnvironment = false,
}) => {
  const loginInfo = useSelector((state: RootState) => state.user);
  const history = useHistory();

  const { onImpersonateUser, IMPERSONATE_TYPE } = useUser();

  const dispatch = useDispatch();

  const { handleOpen: openErrorDialog, ErrorDialog } = useErrorDialog({
    title: undefined,
  });

  const handleClick = () => {
    if (onLeaveClicked) {
      onLeaveClicked();
    }

    userClient
      .getPrevUserByTokens()
      .then(() => {
        dispatch(actions.preventSidebar(true));

        const bc = new BroadcastChannel("impersonate_reload");
        bc.postMessage("reload the tabs.");
        bc.close();

        history.push("/");
      })
      .catch(() => {
        history.push("/logout");
      });
  };

  // Type guard to check if impersonateUser has the expected structure
  const isImpersonateUser = (
    user: ImpersonateUser | Record<string, unknown> | undefined
  ): user is ImpersonateUser => {
    return user !== undefined && typeof user === "object";
  };

  const user = isImpersonateUser(impersonateUser) ? impersonateUser : undefined;

  const impersonating = !!user && !!loginInfo;

  return isDemoEnvironment || impersonating ? (
    <div
      style={{
        zIndex: 1003,
        position: "fixed",
        width: "100%",
        // Colours and height live in impersonate.scss so they come from the design tokens;
        // the impersonation shades stay inline as they were.
        ...(isDemoEnvironment
          ? {}
          : {
              backgroundColor: user?.supportMode ? "rgb(255 168 88)" : "#212851",
              color: user?.supportMode ? "#212851" : "white",
            }),
      }}
      // flex-nowrap, not flex-wrap: every offset under `.impersonate-mode` is
      // derived from $snt-env-bar-height, so this bar has to stay exactly that
      // tall. Wrapping grew it, and since .snt-header is pinned at that same
      // offset and paints above (1004 vs 1003), the wrapped row — the Leave and
      // Admin-mode buttons — disappeared under the header on mobile
      // (STIC-16221). The buttons hold their size and the org name truncates
      // instead; what matters on a phone is the colour and the way out.
      className={`snt-env-bar ${
        isDemoEnvironment ? "snt-env-bar--demo" : ""
      } d-flex align-items-center flex-nowrap justify-content-md-start justify-content-between px-4 py-1`}
    >
      {isDemoEnvironment && (
        <div className="d-flex align-items-center pe-3 flex-shrink-0">
          <span className="fw-bold text-uppercase">
            {translate(LanguageKey.feature_demo_environment_name)}
          </span>
          {/*
            Only when this bar is the demo prefix alone. In the combined state the
            impersonation block below brings its own icon, and that one carries meaning the
            demo prefix's does not — admin vs read-only support — so showing both put two
            identical smileys side by side.
          */}
          {!impersonating && (
            <span className="ps-2 d-none d-md-flex align-items-center">
              <SntSupportUserIcon size={20} />
            </span>
          )}
        </div>
      )}
      {!impersonating ? (
        <></>
      ) : user?.supportMode ? (
        <>
          <div className="d-flex align-items-center" style={{ minWidth: 0 }}>
            <div className="pe-3 d-none d-md-block">
              {user?.adminMode ? (
                <SntSupportAdminIcon size={24} />
              ) : (
                <SntSupportUserIcon size={24} />
              )}
            </div>
            <span className="text-truncate">
              <span className="d-none d-md-inline">
                <span className="fw-bold">{translate(LanguageKey.support_mode_key)}</span>
                {" | "}
              </span>
              {user?.adminMode ? (
                <>{translate(LanguageKey.sidebar_admin_key)}</>
              ) : (
                <>{translate(LanguageKey.read_only_key)}</>
              )}
              {" | "}
              <span
                title={
                  !user?.hasAdminMode && !user?.adminMode
                    ? translate(LanguageKey.viewer_title_key)
                    : ""
                }
              >
                {loginInfo?.organisation?.name}
              </span>
            </span>
          </div>
          <div className="d-flex align-items-center gap-2 ms-3 flex-shrink-0">
            {user?.hasAdminMode && !user?.adminMode ? (
              <SntButton
                variant="sensolus"
                style={
                  isDemoEnvironment
                    ? undefined
                    : { backgroundColor: "#ffa85800", color: "#212851" }
                }
                onClick={() => {
                  onImpersonateUser({
                    id: loginInfo?.organisation?.id ?? 0,
                    type: IMPERSONATE_TYPE.ADMIN,
                    onBadRequest: () => {
                      openErrorDialog(
                        translate(LanguageKey.impersonation_insufficient_rights_key)
                      );
                    },
                  });
                }}
                icon={<SntSupportAdminIcon />}
                text={translate(LanguageKey.admin_mode_key)} trackingKey="admin_mode_key"
              />
            ) : (
              <></>
            )}
            <Button
              variant="sensolus"
              style={
                  isDemoEnvironment
                    ? undefined
                    : { backgroundColor: "#ffa85800", color: "#212851" }
                }
              onClick={handleClick}
            >
              {translate(LanguageKey.leave_key)}
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className="d-flex align-items-center" style={{ minWidth: 0 }}>
            <div className="pe-3 d-none d-md-block">
              <SntImpersonateUserIcon size={24} />
            </div>
            <span className="text-truncate">
              <span className="d-none d-md-inline">
                <span className="fw-bold">{translate(LanguageKey.impersonation_mode)}</span>
                {" | "}
                {translate(LanguageKey.user_key)}: {loginInfo?.fullName}
                {" - "}
              </span>
              {loginInfo?.organisation?.name}
            </span>
          </div>
          <Button
            variant="sensolus"
            style={isDemoEnvironment ? undefined : { borderColor: "white" }}
            className="fw-bold ms-3 flex-shrink-0"
            onClick={handleClick}
          >
            {translate(LanguageKey.leave_key)}
          </Button>
        </>
      )}
      <ErrorDialog />
    </div>
  ) : (
    <></>
  );
};

export default ImpersonateHeader;
